import { REVIEW_TEAM_LABEL } from '@/components/pages/Applicant/components/ApplicantConditions/conditionTemplates';
import { ReviewTeamPills } from '@/components/pages/Applicant/components/ApplicantConditions/ReviewTeamPills';
import { usePermissions } from '@/components/providers/PermissionsContextProvider';
import {
    previewConditionDefinition,
    UpdateConditionDefinitionRequest,
} from '@/components/shared/api/endpoints/conditionDefinitions';
import {
    useCreateConditionDefinitionMutation,
    useUpdateConditionDefinitionMutation,
} from '@/components/shared/api/mutations/conditionDefinitions';
import {
    useConditionDefinitionPlaceholdersQuery,
    useConditionDefinitionsQuery,
    useConditionDefinitionTriggerOptionsQuery,
} from '@/components/shared/api/queries/conditionDefinitions';
import { Pill } from '@/components/shared/components/Pill';
import { TemplateManager } from '@/components/shared/components/TemplateManager';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';
import { Feature } from '@/components/shared/constants/features';
import {
    buildConditionExtraFields,
    ConditionExtraValues,
    hasConfigurableTriggers,
    toTriggerInputs,
} from './ConditionDefinitionExtraFields';
import {
    ConditionDefinitionTemplate,
    toConditionDefinitionTemplate,
} from './conditionDefinitionsAdapter';
import { formatConditionTriggers } from './conditionTriggers';

// Templates group by their first team in display order; one with no team sorts last.
const sortByTeam = (item: ConditionDefinitionTemplate) =>
    item.reviewTeams.length > 0 ? String(item.reviewTeams[0]) : null;

// Mirrors the backend's PlaceholderConstants.ConditionDefinition.OwnerName — the only placeholder the API currently restricts to per-owner templates.
const OWNER_NAME_PLACEHOLDER = 'OWNER_NAME';

export const ConditionTemplatesTab = () => {
    const { hasFeature } = usePermissions();
    const canManage = hasFeature(Feature.ManageTemplates);
    const canEdit = hasFeature(Feature.EditTemplates);
    const { isConfigurableTriggersEnabled } = useFeatureFlags();
    const canEditTriggers = (item: ConditionDefinitionTemplate | null) =>
        isConfigurableTriggersEnabled && canManage && hasConfigurableTriggers(item);

    const {
        data: conditionDefinitions,
        isLoading,
        isError,
        isRefetching,
        refetch,
    } = useConditionDefinitionsQuery();
    const { data: placeholders } = useConditionDefinitionPlaceholdersQuery();
    const { data: triggerOptions } = useConditionDefinitionTriggerOptionsQuery(
        isConfigurableTriggersEnabled && canManage,
    );
    const { mutateAsync: createConditionDefinition } = useCreateConditionDefinitionMutation();
    const { mutateAsync: updateConditionDefinition } = useUpdateConditionDefinitionMutation();

    const items = (conditionDefinitions ?? []).map(toConditionDefinitionTemplate);

    const buildUpdateRequest = (
        item: ConditionDefinitionTemplate,
        overrides: Partial<UpdateConditionDefinitionRequest> = {},
    ): UpdateConditionDefinitionRequest => ({
        label: item.label,
        descriptionTemplate: item.body,
        defaultProducts: item.defaultProducts,
        referenceProducts: item.referenceProducts,
        reviewTeams: item.reviewTeams,
        isActive: item.isActive,
        rowVersion: item.rowVersion,
        triggers: null,
        ...overrides,
    });

    return (
        <TemplateManager<ConditionDefinitionTemplate, ConditionExtraValues>
            title="Task/Condition Template"
            items={items}
            placeholders={(editingItem) =>
                editingItem?.isPerOwner
                    ? (placeholders ?? [])
                    : (placeholders ?? []).filter((p) => p !== OWNER_NAME_PLACEHOLDER)
            }
            isLoading={isLoading}
            isLoadingError={isError}
            isRefetching={isRefetching}
            onRetry={refetch}
            canManage={canManage}
            canEdit={canEdit}
            extraBadge={(item) => (
                <>
                    {item.isPerOwner ? <Pill variant="blue">Per-Owner</Pill> : null}
                    {item.hasAutoComment ? <Pill variant="violet">Auto-Comment</Pill> : null}
                </>
            )}
            extraColumns={[
                {
                    header: 'Trigger',
                    widthPercent: 22,
                    body: (item) => formatConditionTriggers(item.triggers),
                    sortValue: (item) =>
                        item.triggers.length > 0 ? formatConditionTriggers(item.triggers) : null,
                },
                {
                    header: 'Team',
                    widthPercent: 15,
                    body: (item) =>
                        item.reviewTeams.length > 0 ? <ReviewTeamPills teams={item.reviewTeams} /> : '—',
                    sortValue: (item) =>
                        item.reviewTeams.length > 0 ? REVIEW_TEAM_LABEL[item.reviewTeams[0]] : null,
                },
            ]}
            sortGroup={sortByTeam}
            // Coded and triggered rows back automated condition creation, which silently stops if they're retired.
            canRetire={(item) => item.code === null && item.triggers.length === 0}
            extraFields={buildConditionExtraFields(canEditTriggers, triggerOptions)}
            onCreate={async (values) => {
                const dto = await createConditionDefinition({
                    label: values.label,
                    descriptionTemplate: values.body,
                    defaultProducts: values.defaultProducts,
                    // New templates are never coded, and nothing reads an uncoded row's reference products.
                    referenceProducts: values.defaultProducts,
                    reviewTeams: values.reviewTeams,
                    triggers: canEditTriggers(null) ? toTriggerInputs(values) : null,
                });
                return toConditionDefinitionTemplate(dto);
            }}
            onUpdate={async (id, values) => {
                const existing = items.find((item) => item.id === id);
                const dto = await updateConditionDefinition({
                    id,
                    request: {
                        label: values.label,
                        descriptionTemplate: values.body,
                        defaultProducts: values.defaultProducts,
                        referenceProducts:
                            existing?.code != null ? values.referenceProducts : values.defaultProducts,
                        reviewTeams: values.reviewTeams,
                        isActive: existing?.isActive ?? true,
                        rowVersion: existing?.rowVersion ?? 0,
                        triggers:
                            existing && canEditTriggers(existing) ? toTriggerInputs(values) : null,
                    },
                });
                return toConditionDefinitionTemplate(dto);
            }}
            onRetireItem={async (item) => {
                const dto = await updateConditionDefinition({
                    id: item.id,
                    request: buildUpdateRequest(item, { isActive: false }),
                });
                return toConditionDefinitionTemplate(dto);
            }}
            onReactivateItem={async (item) => {
                const dto = await updateConditionDefinition({
                    id: item.id,
                    request: buildUpdateRequest(item, { isActive: true }),
                });
                return toConditionDefinitionTemplate(dto);
            }}
            onPreview={async (body, editingItem) =>
                previewConditionDefinition(body, editingItem?.isPerOwner ?? false)
            }
        />
    );
};
